// API Configuration for Stasht Platform
import { CONFIG } from "@/config";

const RUNTIME_CONFIG_KEYS = {
  API_URL: "stasht.api.url",
  API_KEY: "stasht.api.key",
} as const;

const readRuntimeValue = (key: string): string | undefined => {
  if (typeof window === "undefined") return undefined;
  const runtime = (window as { __STASHT_RUNTIME__?: Record<string, string> }).__STASHT_RUNTIME__;
  return runtime?.[key] || window.localStorage.getItem(key) || undefined;
};

const isLocalHost = (): boolean => {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname.endsWith(".localhost") ||
    hostname === "local.stasht.app"
  );
};

const resolveApiUrl = (): string => {
  const envValue = CONFIG.API_URL;
  if (CONFIG.IS_STAGING_HOST) {
    if (!envValue) {
      throw new Error("staging.stasht.app requires a staging API URL");
    }
    return envValue;
  }
  if (isLocalHost()) {
    if (!envValue) {
      throw new Error("VITE_API_URL environment variable is required for local development");
    }
    return envValue;
  }
  const runtimeValue = readRuntimeValue(RUNTIME_CONFIG_KEYS.API_URL);
  const resolved = runtimeValue || envValue;
  if (!resolved) {
    throw new Error("API_URL environment variable is required");
  }
  return resolved;
};

const resolveApiKey = (): string | undefined => {
  return readRuntimeValue(RUNTIME_CONFIG_KEYS.API_KEY) || CONFIG.API_KEY;
};

// ALWAYS USE NEW INFRASTRUCTURE - NO MORE OLD API
const USE_NEW_INFRA = true; // FORCE NEW INFRA ALWAYS

export const API_CONFIG = {
  // Single clean API URL - no stages
  get BASE_URL() {
    return resolveApiUrl();
  },

  // Same as BASE_URL for consistency
  get NEW_API_URL() {
    return resolveApiUrl();
  },

  // Rollout status
  USING_NEW_INFRA: USE_NEW_INFRA,

  // API Key for public endpoints - REQUIRED
  get API_KEY() {
    return resolveApiKey();
  },

  // Request timeout in milliseconds
  TIMEOUT: 30000, // 30 seconds to allow for Claude response generation

  // Retry configuration
  RETRY: {
    MAX_ATTEMPTS: 1, // No retries for normal requests - API is just slow
    INITIAL_DELAY: 1000,
    MAX_DELAY: 10000,
    BACKOFF_FACTOR: 2,
  },

  // Rate limiting (client-side throttling)
  RATE_LIMIT: {
    MAX_REQUESTS_PER_MINUTE: 200,
    MAX_REQUESTS_PER_HOUR: 2000,
  },
} as const;

// API Endpoints
export const ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: "/auth/login",
    REGISTER: "/auth/register",
    ACTIVATE: "/auth/activate",
    WAITLIST_UPDATE_PROGRESS: "/auth/waitlist/update-progress",
    ONBOARDING_PROGRESS: "/auth/onboarding/progress",
    REFRESH: "/auth/refresh",
    LOGOUT: "/auth/logout",
    ACTIVITY: "/auth/activity",
    PROFILE: "/auth/profile",
    DELETE_ACCOUNT: "/auth/delete-account",
    UPDATE_PASSWORD: "/auth/update-password",
    SET_PASSWORD: "/auth/set-password",
    SIGN_IN_METHODS: "/auth/sign-in-methods",
    FORGOT_PASSWORD: "/auth/forgot-password",
    RESET_PASSWORD_VALIDATE: "/auth/reset-password/validate",
    RESET_PASSWORD: "/auth/reset-password",
    VERIFY_EMAIL: "/auth/verify-email",
    RESEND_VERIFICATION: "/auth/resend-verification",
    EMAIL_OTP_REQUEST: "/auth/email-otp/request",
    EMAIL_OTP_VERIFY: "/auth/email-otp/verify",
    X_CONNECT: "/auth/x/connect",
    X_STATUS: "/auth/x/status",
    X_DISCONNECT: "/auth/x/disconnect",
    X_BOOKMARKS_IMPORT: "/auth/x/bookmarks/import",
    GOOGLE_CONNECT: "/auth/google/connect",
    GOOGLE_STATUS: "/auth/google/status",
    GOOGLE_DISCONNECT: "/auth/google/disconnect",
    APPLE_CONNECT: "/auth/apple/connect",
    APPLE_STATUS: "/auth/apple/status",
    APPLE_DISCONNECT: "/auth/apple/disconnect",
    SOCIAL_LOGIN: "/auth/social/login",
    QR_CREATE_SESSION: "/auth/qr/session",
    QR_SESSION_STATUS: "/auth/qr/session",
    QR_VERIFY: "/auth/qr/verify",
    QR_CONFIRM: "/auth/qr/confirm",
  },

  // User data endpoints
  POSTS: {
    LIST: "/posts",
    GET: (id: string) => `/posts/${id}`,
    BULK_VISIBILITY: "/posts/bulk-visibility",
  },

  // Profile endpoints
  PROFILE: {
    GET: "/profile",
    UPDATE: "/profile",
    CHECK_USERNAME: "/profile/username-check",
    CHECK_EMAIL: "/profile/email-check",
    SETUP: "/auth/profile/setup",
    AVATAR_UPLOAD: "/profile/avatar-upload",
    LOOKUP_USERS: "/profile/users/lookup",
  },

  INVITES: {
    LIST: "/invites",
    TRACK: "/invites/track",
  },

  // Entity endpoints
  ENTITIES: {
    SEARCH: "/entities/search",
    GET: (id: string) => `/entities/${id}`,
    TRENDING: "/entities/trending",
  },

  // Events endpoints
  EVENTS: {
    LIST: "/events",
    GET: (id: string) => `/events/${id}`,
  },

  // Collections
  COLLECTIONS: {
    LIST: "/collections",
    PUBLIC: "/collections/public",
    GET: (id: string) => `/collections/${id}`,
    CREATE: "/collections",
    UPDATE: (id: string) => `/collections/${id}`,
    DELETE: (id: string) => `/collections/${id}`,
    ITEMS: (id: string) => `/collections/${id}/items`,
    FOR_STASH: (stashId: string) => `/collections/for-stash/${stashId}`,
    FOR_ENTITY: (entityId: string) => `/collections/for-entity/${entityId}`,
    MEMBERS: (id: string) => `/collections/${id}/members`,
    MEMBER: (id: string, userId: string) => `/collections/${id}/members/${userId}`,
    INVITE_EXTERNAL: (id: string) => `/collections/${id}/invite-external`,
    CLAIM_INVITE: (id: string) => `/collections/${id}/claim-invite`,
    INVITE_LINK: (id: string) => `/collections/${id}/invite-link`,
    RECOMMENDATIONS: (id: string) => `/collections/${id}/recommendations`,
    FOLLOW: (id: string) => `/collections/${id}/follow`,
  },

  // Analytics endpoints
  ANALYTICS: {
    METRICS: "/analytics/metrics",
    CATEGORIES: "/analytics/categories",
    ACHIEVEMENTS: "/achievements",
  },

  NOTIFICATIONS: {
    REMINDERS: "/notifications/reminders",
    DEVICE_TOKEN: "/notifications/device-token",
    PREFERENCES: "/notifications/preferences",
    EMAIL_PREFERENCES: "/notifications/email-preferences",
    UNSUBSCRIBE: "/notifications/unsubscribe",
    INBOX: "/notifications/inbox",
    UPCOMING: "/notifications/upcoming",
    CANCEL: "/notifications/cancel",
    SNOOZE: "/notifications/snooze",
  },

  ROUNDUPS: {
    LIST: "/roundups",
    GET: (id: string) => `/roundups/${id}`,
    ACTIVITY: "/roundups/activity",
  },

  CAMPAIGN_LANDING: {
    GET: (id: string) => `/campaigns/${id}/landing`,
    EVENTS: (id: string) => `/campaigns/${id}/events`,
  },

  DECK: {
    ACCESS: (version: string) => `/deck/${version}/access`,
    EVENTS: "/deck/events",
  },

  // Chat endpoint
  CHAT: "/chat",

} as const;

// Security headers - lowercase for API Gateway v2 compatibility
export const SECURITY_HEADERS = {
  get "x-api-key"() {
    return API_CONFIG.API_KEY;
  },
  "x-client-version": "1.0.0",
  "x-request-id": () => crypto.randomUUID(),
};

// Token storage keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: "stasht_access_token",
  REFRESH_TOKEN: "stasht_refresh_token",
  USER_DATA: "stasht_user_data",
  INVITE_CODE: "stasht_invite_code",
  INVITE_INSTALL_CHECKED: "stasht_invite_install_checked",
  SESSION_ACTIVE: "stasht_session_active",
  // Version-based keys for invalidation
  TOKEN_VERSION: "stasht_token_version",
  API_VERSION: "stasht_api_version",
} as const;

// Shared contract for iOS install-referrer clipboard handoff.
// Writer (InviteLanding) and reader (App) must agree on this exact shape.
// Body is the canonical 16-char Crockford base32 invite code (no INV prefix).
export const CLIPBOARD_INVITE_PREFIX = "stasht-invite:";
export const CLIPBOARD_INVITE_REGEX = /^stasht-invite:([0-9A-HJKMNPQRSTVWXYZ]{16})$/;

// Current API version - bump this when making breaking changes
export const API_VERSION = "v2.0.1";

// Error codes
export const ERROR_CODES = {
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  RATE_LIMITED: "RATE_LIMITED",
  VALIDATION_ERROR: "VALIDATION_ERROR",
  SERVER_ERROR: "SERVER_ERROR",
  NETWORK_ERROR: "NETWORK_ERROR",
} as const;
